import React, { useCallback, useContext, useMemo, useState } from 'react'
import cn from 'classnames'

import { Typography } from '@grammarly/badger-ui'
import { ContentProps, ContentRenderer } from '@components/renderer'
import { PageSection } from '@components/page-section'
import { PageContext } from '@contexts/page-context'
import { TypeVariationConditionalSelect } from '@lib/types/conditional-select'
import { isTypeVariationConditionalSelect } from '@lib/types/content-types'
import { gnarSpec } from '@lib/tracking/gnar'

import { Select } from './select'
import styles from './conditional-select.module.scss'

export const VariationConditionalSelect: React.FC<ContentProps<TypeVariationConditionalSelect>> = ({ structure }) => {
  const { pageId, slug } = useContext(PageContext)

  const { label, defaultValue = '', conditionalSelectItems } = structure.fields

  const [selectedOption, setSelectedOption] = useState(defaultValue)

  const options = conditionalSelectItems.map((val) => val.fields.value).filter(Boolean)

  const getOptionItem = useCallback(
    (optionValue: string) => {
      return conditionalSelectItems.find((val) => {
        const isMatchedOptionValue = val.fields.value === optionValue
        const isDefaultOption = !val.fields.value && !optionValue

        return isMatchedOptionValue || isDefaultOption
      })
    },
    [conditionalSelectItems]
  )

  const onOptionSelect = (optionValue: string) => {
    setSelectedOption(optionValue)

    const item = getOptionItem(optionValue)

    if (!item) {
      return
    }

    gnarSpec.itemDropdownVariationClick(item.fields.internalName, optionValue, pageId, slug)
  }

  const selectedItem = useMemo(() => {
    const item = getOptionItem(selectedOption)

    if (!item) {
      return { structure: [], hasNestedChildConditional: false }
    }

    const structure = item.fields.content
    return { structure, hasNestedChildConditional: isTypeVariationConditionalSelect(structure[0]) }
  }, [getOptionItem, selectedOption])

  return (
    <>
      <PageSection
        className={styles.pageSection}
        internalName={structure.fields.internalName}
        sysStructure={structure.sys}
      >
        <div
          className={cn(styles.wrapper, {
            [styles.parentConditionalContent]: selectedItem.hasNestedChildConditional,
          })}
        >
          <Typography.HS as="h2" className={styles.label}>
            {label}
          </Typography.HS>

          <Select
            className={styles.select}
            value={selectedOption}
            options={options}
            onChange={(e) => onOptionSelect(e.target.value)}
          />
        </div>
      </PageSection>

      <ContentRenderer structure={selectedItem.structure} />
    </>
  )
}
